import { FunctionComponent, ReactNode, useEffect } from "react"
import { Outlet } from "react-router"
import { useMatch } from "react-router-dom"
import { styled } from "@mui/material"
import { Box } from "@mui/material"
import Sidebar, { Props as SidebarProps } from "components/Sidebar"
import Header from "components/Header"
import { HEADER_HEIGHT } from "@client-hub/shared/constants/styles"
import { Props as HeaderProps } from "components/Header"

export type OutletWrapperProps = {
  /**
   * This specifies whether the user is on home route. If user is not on home route, then
   * there should be left margin of 340px
   */
  matchBasePath: boolean

  /**
   * This specifies whether the user is on profile route with user permissions.
   * If user is not on profile route WITHOUT PERMISSION, then
   * there should not be left margin of 340px
   */
  matchProfilePathWithPermission: boolean
  /**
   * This specifies the component render in OutletWrapper
   */
  component: ReactNode
  /**
   * General purpose boolean, which if "true", will facilitate skipping the left margin
   */
  leaveNoMargin?: boolean
}

const OutletWrapper = styled(Box)(() => ({
  marginTop: HEADER_HEIGHT,
  display: "flex",
  width: "100%",
  height: "fit-content",
}))

const ContentWrapper = styled(Box)(() => ({
  width: "100%",
  height: "100vh",
  display: "flex",
  overflow: "auto",
}))

const StyledSidebar = styled(Box)(({ theme: { spacing } }) => ({
  width: "fit-content",
  position: "sticky",
  top: 0,
  left: 0,
  padding: `calc(${HEADER_HEIGHT} + 2.6rem) 0 0 ${spacing(3)}`,
}))

type Props = {
  header: Omit<HeaderProps, "logoLink">
  sidebar: SidebarProps
  basePath: string
}

const LayoutBase: FunctionComponent<Props> = ({ header, sidebar, basePath }) => {
  const matchBasePath = !!useMatch(basePath)

  /**
   * Resets scroll position whenever the route is changed
   */
  useEffect(() => {
    window.scroll({
      top: 0,
      left: 0,
      behavior: "smooth",
    })
  }, [])

  return (
    <Box sx={{ display: "flex" }}>
      <Header {...header} logoLink={basePath} />

      <ContentWrapper>
        <StyledSidebar>{!matchBasePath && <Sidebar {...sidebar} />}</StyledSidebar>

        <OutletWrapper component="main">
          <Outlet />
        </OutletWrapper>
      </ContentWrapper>
    </Box>
  )
}

export default LayoutBase
